import type { Rng } from '@gardener/core';
import type { PaletteKey } from '../palette';
import { type Pixel, parseGrid } from './grid';

/**
 * An indexed (palette-locked) pixel canvas used by procedural sprites. Out-of-bounds writes are ignored,
 * so shapes may overhang the edges.
 */
export class PixelCanvas {
  readonly w: number;
  readonly h: number;
  readonly pixels: Pixel[];

  constructor(w: number, h: number) {
    this.w = w;
    this.h = h;
    this.pixels = new Array<Pixel>(w * h).fill(null);
  }

  static fromGrid(src: string, where?: string): PixelCanvas {
    const g = parseGrid(src, where);
    const c = new PixelCanvas(g.w, g.h);
    g.pixels.forEach((p, i) => {
      c.pixels[i] = p;
    });
    return c;
  }

  inBounds(x: number, y: number): boolean {
    return x >= 0 && y >= 0 && x < this.w && y < this.h;
  }

  get(x: number, y: number): Pixel {
    return this.inBounds(x, y) ? (this.pixels[y * this.w + x] as Pixel) : null;
  }

  set(x: number, y: number, c: Pixel): this {
    x = Math.floor(x);
    y = Math.floor(y);
    if (this.inBounds(x, y)) this.pixels[y * this.w + x] = c;
    return this;
  }

  /** Sets only where the canvas is currently opaque (paint "inside" a shape). */
  tint(x: number, y: number, c: PaletteKey): this {
    if (this.get(x, y) !== null) this.set(x, y, c);
    return this;
  }

  fill(c: Pixel): this {
    this.pixels.fill(c);
    return this;
  }

  rect(x: number, y: number, w: number, h: number, c: Pixel): this {
    for (let yy = y; yy < y + h; yy++) for (let xx = x; xx < x + w; xx++) this.set(xx, yy, c);
    return this;
  }

  strokeRect(x: number, y: number, w: number, h: number, c: Pixel): this {
    this.hline(x, x + w - 1, y, c).hline(x, x + w - 1, y + h - 1, c);
    return this.vline(x, y, y + h - 1, c).vline(x + w - 1, y, y + h - 1, c);
  }

  hline(x0: number, x1: number, y: number, c: Pixel): this {
    for (let x = Math.min(x0, x1); x <= Math.max(x0, x1); x++) this.set(x, y, c);
    return this;
  }

  vline(x: number, y0: number, y1: number, c: Pixel): this {
    for (let y = Math.min(y0, y1); y <= Math.max(y0, y1); y++) this.set(x, y, c);
    return this;
  }

  /** Bresenham line. */
  line(x0: number, y0: number, x1: number, y1: number, c: Pixel): this {
    x0 = Math.round(x0);
    y0 = Math.round(y0);
    x1 = Math.round(x1);
    y1 = Math.round(y1);
    const dx = Math.abs(x1 - x0);
    const dy = -Math.abs(y1 - y0);
    const sx = x0 < x1 ? 1 : -1;
    const sy = y0 < y1 ? 1 : -1;
    let err = dx + dy;
    for (;;) {
      this.set(x0, y0, c);
      if (x0 === x1 && y0 === y1) break;
      const e2 = 2 * err;
      if (e2 >= dy) {
        err += dy;
        x0 += sx;
      }
      if (e2 <= dx) {
        err += dx;
        y0 += sy;
      }
    }
    return this;
  }

  /** Filled ellipse centred on (cx, cy) — use .5 centres for even sizes. */
  ellipse(cx: number, cy: number, rx: number, ry: number, c: Pixel): this {
    for (let y = Math.floor(cy - ry); y <= Math.ceil(cy + ry); y++) {
      for (let x = Math.floor(cx - rx); x <= Math.ceil(cx + rx); x++) {
        const nx = (x + 0.5 - cx) / rx;
        const ny = (y + 0.5 - cy) / ry;
        if (nx * nx + ny * ny <= 1) this.set(x, y, c);
      }
    }
    return this;
  }

  circle(cx: number, cy: number, r: number, c: Pixel): this {
    return this.ellipse(cx, cy, r, r, c);
  }

  /** Draws a text grid at (x, y); transparent cells are skipped. */
  stamp(src: string, x = 0, y = 0): this {
    const g = parseGrid(src, 'stamp');
    for (let yy = 0; yy < g.h; yy++) {
      for (let xx = 0; xx < g.w; xx++) {
        const p = g.pixels[yy * g.w + xx] as Pixel;
        if (p !== null) this.set(x + xx, y + yy, p);
      }
    }
    return this;
  }

  /** Copies another canvas onto this one (transparent pixels skipped). */
  blit(src: PixelCanvas, x = 0, y = 0): this {
    for (let yy = 0; yy < src.h; yy++) {
      for (let xx = 0; xx < src.w; xx++) {
        const p = src.get(xx, yy);
        if (p !== null) this.set(x + xx, y + yy, p);
      }
    }
    return this;
  }

  replace(from: Pixel, to: Pixel): this {
    for (let i = 0; i < this.pixels.length; i++) if (this.pixels[i] === from) this.pixels[i] = to;
    return this;
  }

  /** Adds a 1px outline of colour `c` around opaque pixels (4-neighbourhood, or 8 with `diagonal`). */
  outline(c: PaletteKey, diagonal = false): this {
    const add: number[] = [];
    for (let y = 0; y < this.h; y++) {
      for (let x = 0; x < this.w; x++) {
        if (this.get(x, y) !== null) continue;
        const n =
          this.get(x - 1, y) !== null ||
          this.get(x + 1, y) !== null ||
          this.get(x, y - 1) !== null ||
          this.get(x, y + 1) !== null ||
          (diagonal &&
            (this.get(x - 1, y - 1) !== null ||
              this.get(x + 1, y - 1) !== null ||
              this.get(x - 1, y + 1) !== null ||
              this.get(x + 1, y + 1) !== null));
        if (n) add.push(y * this.w + x);
      }
    }
    for (const i of add) this.pixels[i] = c;
    return this;
  }

  /** Scatters `count` pixels of `c` at random positions where `where(x, y)` holds. */
  scatter(rng: Rng, count: number, c: Pixel, where: (x: number, y: number) => boolean = () => true): this {
    for (let n = 0, tries = 0; n < count && tries < count * 20; tries++) {
      const x = rng.int(0, this.w - 1);
      const y = rng.int(0, this.h - 1);
      if (!where(x, y)) continue;
      this.set(x, y, c);
      n++;
    }
    return this;
  }

  /**
   * Twice the size by Scale2x (EPX): a pixel becomes 2×2, its corners taking a neighbour's colour where two agree, so
   * diagonals stay 1 px steps instead of 2 px stairs. A starting point for redrawing art at a higher density.
   */
  scale2x(): PixelCanvas {
    const out = new PixelCanvas(this.w * 2, this.h * 2);
    for (let y = 0; y < this.h; y++) {
      for (let x = 0; x < this.w; x++) {
        const p = this.get(x, y);
        const [a, b, c, d] = [this.get(x, y - 1), this.get(x + 1, y), this.get(x - 1, y), this.get(x, y + 1)];
        out.set(2 * x, 2 * y, c === a && c !== d && a !== b ? a : p);
        out.set(2 * x + 1, 2 * y, a === b && a !== c && b !== d ? b : p);
        out.set(2 * x, 2 * y + 1, d === c && d !== b && c !== a ? c : p);
        out.set(2 * x + 1, 2 * y + 1, b === d && b !== a && d !== c ? d : p);
      }
    }
    return out;
  }

  flipX(): PixelCanvas {
    const out = new PixelCanvas(this.w, this.h);
    for (let y = 0; y < this.h; y++) for (let x = 0; x < this.w; x++) out.set(this.w - 1 - x, y, this.get(x, y));
    return out;
  }

  clone(): PixelCanvas {
    const out = new PixelCanvas(this.w, this.h);
    this.pixels.forEach((p, i) => {
      out.pixels[i] = p;
    });
    return out;
  }
}
